import React, { FC } from 'react';
import { Space, Spin, Typography } from 'antd';
import { Trans, useTranslation } from 'react-i18next';
import styled from 'styled-components';
import colors from '~/constants/colors';

import { UploadIcon, UploadIconSVG } from '~/img';
import humanFileSize from '~/utils/humanFileSize';
import direction from '~/utils/direction';
import { DirectionProp } from '~/types/Direction';
import { useDirection } from '~/hooks/i18nHooks';
import OptionalLabel from './OptionalLabel';

interface Props {
  maxSizeB: number;
  id?: string;
  loading: boolean;
  optional?: boolean;
  labelText?: string;
  styleType?: string;
  infoIcon?: React.ReactNode;
}

const DocumentInputContent: FC<Props> = (props) => {
  const { maxSizeB, id, loading, optional, labelText = '', styleType, infoIcon } = props;
  const { t } = useTranslation();
  const dir = useDirection();

  if (loading) return <Spin />;

  return (
    <>
      <Container id={id} className="DocumentInputContent" dir={dir}>
        <Space size={12} className="DocumentInputContent-TopText">
          <Typography.Paragraph className="DocumentInputContent-Paragraph">
            {t(labelText)}
          </Typography.Paragraph>
        </Space>
        <UploadIconSVG className="DocumentInputContent-UploadIcon" />
        {infoIcon && <span className="DocumentInputContent-InfoIcon">{infoIcon}</span>}
      </Container>
    </>
  );
};

const ContractContainer = styled.div<any>`
  .DocumentInputContent-UploadIcon {
    transform: translate(-50%, -50%);
    background: transparent !important;
    font-size: 36px;
    color: transparent;
    position: absolute;
    top: 50%;
    left: 50%;
  }
`;

const Container = styled.div<DirectionProp>`
  width: 100%;
  height: 100%;
  border-radius: 8px;
  background: white;
  padding: 16px;
  position: relative;
  direction: ${({ dir }) => dir || 'ltr'};
  text-align: ${({ dir }) => (dir === 'rtl' ? 'right' : 'left')};

  .DocumentInputContent-TopText {
    width: 100%;
    display: flex;
    justify-content: ${({ dir }) => (dir === 'rtl' ? 'flex-start' : 'flex-start')};
    .ant-space-item {
      width: 100%;
    }
  }

  .DocumentInputContent-Paragraph {
    color: #1b1b20;
    font-family: ${({ dir }) => (dir === 'rtl' ? 'Tajawal' : 'Inter')}, sans-serif;
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: 24px; /* 171.429% */
    letter-spacing: 0.14px;
    text-align: ${({ dir }) => (dir === 'rtl' ? 'right' : 'left')};

    /* place paragraph at top — flips side based on direction */
    position: absolute;
    top: 12px;
    left: ${({ dir }) => (dir === 'rtl' ? 'auto' : '16px')};
    right: ${({ dir }) => (dir === 'rtl' ? '16px' : 'auto')};
    margin: 0;
  }

  .DocumentInputContent-UploadIcon {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    background: transparent !important;
    font-size: 36px;
    color: transparent;
  }

  /* Info icon — flips to bottom-left in RTL */
  .DocumentInputContent-InfoIcon {
    position: absolute;
    bottom: 12px;
    right: ${({ dir }) => (dir === 'rtl' ? 'auto' : '12px')};
    left: ${({ dir }) => (dir === 'rtl' ? '12px' : 'auto')};
    display: flex;
    align-items: center;
    justify-content: center;
    pointer-events: none;
    line-height: 1;
  }
`;

export default DocumentInputContent;
